import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { store, history } from '../../_helpers'
import { Modal, Card, Header, Body, DashboardTemplate, ChevronRightIcon, ChevronLeftIcon, ChevronUpIcon, StartSolidRoundedIcon, ClockIcon, PencilSolidIcon, CalendarIcon } from '../../_components'
import { Filter, Trigger, Channel, Finish, Confirmation } from './Cards'
import { MomentumWeekDaySelect } from './Cards/MomentumWeekDaySelect'
import { validateFields, removeErrorDisplay, saveMomentum } from './_functions'
import DatePicker, { registerLocale } from 'react-datepicker';
import moment from 'moment';
import ptBR from 'date-fns/locale/pt-BR';
import styles from './styles.module.scss'
import qs from 'qs'

registerLocale('pt-BR', ptBR);

export const MomentumBuilder = ( { match, location } ) => {
  const [ queryType, setQueryType ] = useState("momentum")
  const [ viewOnly, setViewOnly ] = useState(false)
  const paramsData = useMemo( () => {
    const paramsString = location.search.replace( /^\?/, '' )
    const params = qs.parse( paramsString )
    return params
  }, [ location ] )
  const [ data, setData ] = useState( paramsData )
  const [ errors, setErrors ] = useState({})
  const [ domain, setDomain ] = useState()
  const [ submitting, setSubmitting ] = useState(false)
  const [ modal, setModal ] = useState(false)
  const [ scheduleTime, setScheduleTime ] = useState()
  const [ newGoingToBack, setNewGoingToBack ] = useState( false )
  const isEdit = useMemo( () => !!data.uuid, [ data ] )
  const isDraft = useMemo( () => data?.status === 'Draft', [ data ] )
  const disableFilter = useMemo( () => isEdit && !isDraft, [ isEdit, isDraft ] )
  const disableTrigger = useMemo( () => isEdit && !isDraft, [ isEdit, isDraft ] )

  useEffect( () => {
    if (
      data.trigger?.filter === 'pageview'
      &&
      data.trigger.value?.length
      &&
      data.trigger.value.some( value => !value.startsWith( 'https://' ) )
    ) {
      const value = data.trigger.value.map( value => {
        if ( !value.startsWith( 'https://' ) )
          return value.replace( /^(https?:\/\/)?/, 'https://' )

        return value
      } )
      const raw_trigger_value = value.join( ', ' )
      setData( data => ( {
        ...data,
        raw_trigger_value,
        trigger: { ...data.trigger, value }
      } ) )
    }
  }, [ data ] )

  useEffect(() => {
    if ( history.location.pathname.indexOf('campaign') !== -1 ) {
      setQueryType("campaign")
    }

    if ( history.location.search.indexOf("viewonly") !== -1 ) {
      setSubmitting(true)
      setViewOnly(true)
    }

    const domainUuid = store.getState().changeDomainReducer.selectedDomain.domain_uuid
    setDomain(domainUuid)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history.location.pathname, history.location.search])

  const handleChange = ({target}) => {
    removeErrorDisplay("momentum_title", errors, setErrors)
    setData({...data, [target.name]: target.value})
  }
  
  const handleFieldValidations = () => {
    const allFieldsAreValid = validateFields(data, errors, setErrors, queryType)

    if ( !allFieldsAreValid ) return

    setSubmitting(!submitting)
  }

  const handleDateChange = (date) => {
    if ( !date ) return

    const value = moment(date).format("YYYY-MM-DDTHH:mm:ss") + '.000Z'
    setScheduleTime({raw: date, parsed: value})
  }

  const handleGoToBack = useCallback( () => {
    if ( newGoingToBack ) return history.goBack()

    if ( !Object.keys( data ).length ) return history.goBack()

    if ( isEdit && !isDraft ) return history.goBack()

    setNewGoingToBack( true )
  }, [ newGoingToBack, data, isEdit, isDraft ] )

  return (<>
    <DashboardTemplate>
      <Header title={queryType === "momentum" ? "Momentum" : "Campanha"}/>
      <Body>
        {
          !viewOnly ? <>

            <div className={`${styles['filters__wrapper']} ${!submitting && styles['show']}`}>
              <Card
                title={queryType === "momentum" ? "Título do Momentum" : "Título da Campanha"}
                icon="PencilSolidIcon"
                required={true}
              >
                <div className="flex">
                  <div className="w-full">
                    <div
                      className={`
                        inputable__wrapper
                        ${errors.momentumTitle && "inputable__wrapper--error"}
                      `}
                    >
                      <input
                        type="text"
                        name="momentum_title"
                        disabled={isEdit && !isDraft}
                        className="inputable"
                        defaultValue={data.momentum_title}
                        placeholder={`Defina um nome único para ${queryType === "momentum" ? "seu Momentum" : "sua Campanha"}`}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                </div>
              </Card>
            </div>

            <hr className={styles['spacer']}/>

            <div className={`${styles['filters__wrapper']} ${!submitting && styles['show']}`}>
              <Filter disabled={disableFilter} data={data} setData={setData} errors={errors} setErrors={setErrors} domain={domain}/>
              
              {
                queryType === "momentum" &&
                  <Trigger disabled={disableTrigger} data={data} setData={setData} errors={errors} setErrors={setErrors} domain={domain}/>
              }
              <MomentumWeekDaySelect errors={errors} data={data} setData={setData} queryType={queryType} />

              <Channel data={data} setData={setData} errors={errors} setErrors={setErrors} domain={domain}/>

              {
                queryType === "momentum" &&
                  <Finish data={data} setData={setData} errors={errors} setErrors={setErrors} domain={domain}/>
              }
            </div>
            <div className={`${styles['filters__wrapper']} ${submitting && styles['show']}`}>
              <Confirmation receivedData={data} queryType={queryType} />
            </div>

            <button
              type="button"
              className={`button button__with-icon button--uppercase ${!submitting ? 'button--blue button--fr' : 'button--white-outlined button--fl'}`}
              onClick={() => {
                if ( !submitting ) return handleFieldValidations()
                return setSubmitting( false )
              }}
            >
              {
                !submitting ?
                  <><span>Seguir</span><ChevronRightIcon /></>
                : <><ChevronLeftIcon /><span>Voltar</span></>
              }
            </button>

            {
              data && data.length !== 0 && <>
                {
                  !submitting &&
                  <div className="button button__with-icon button--uppercase button--white-outlined button--fl" onClick={handleGoToBack}>
                    <ChevronLeftIcon />
                    <span>Voltar</span>
                  </div>
                }

                {
                  submitting ? ( ( isEdit && isDraft ) || !isEdit )
                    ?
                      <ul className="dropdown dropdown__button dropdown__button--blue elem__fr">
                        <ul>
                          <li onClick={() => saveMomentum(data, domain, 'Active', false, queryType)}><StartSolidRoundedIcon /> Imediato</li>
                          <li onClick={() => setModal(!modal)}>
                            <ClockIcon /> Agendamento
                          </li>
                          <li onClick={() => saveMomentum(data, domain, 'Draft', false, queryType)}><PencilSolidIcon /> Rascunho</li>
                        </ul>

                        <li><span>Planejar campanha</span><ChevronUpIcon /></li>
                      </ul>
                    : (
                      <div className="button elem__fr button__with-icon button--uppercase button--white-outlined button--fl" onClick={() => saveMomentum(data, domain, data.status, false, queryType)}>
                        <StartSolidRoundedIcon />
                        <span>Salvar</span>
                      </div>
                    )
                  : null
                }
              </>
            }
          </> : <Confirmation viewOnly={viewOnly}/>
        }
      </Body>
    </DashboardTemplate>

    <Modal
      active={{ status: newGoingToBack,  onChange: setNewGoingToBack }}
      width="711px"
    >
      <div>{!isDraft ? 'Gostaria de salvar como rascunho?' : 'Deseja salvar as alterações?' }</div>
      <button className='button button__with-icon button--uppercase button--white-outlined button--fl' onClick={handleGoToBack}>
        Não
      </button>
      <button className='button elem__fr button__with-icon button--uppercase button--white-outlined button--fl' onClick={ () => {
        saveMomentum( data, domain, 'Draft', false, queryType ).then( handleGoToBack )
      } }>
        Sim
      </button>
    </Modal>

    {/* Schedule Modal */}
    <Modal
      active={{
        status: modal, 
        onChange: setModal
      }}
      width="711px"
    >
      <div className={styles['modal__confirmation']}>
        <p className={styles['inline']}><CalendarIcon /> Data de agendamento</p>
        <DatePicker
          className="inputable"
          selected={scheduleTime ? scheduleTime.raw : ""}
          defaultValue={scheduleTime ? scheduleTime.raw : ""}
          onChange={handleDateChange}
          minDate={new Date()}
          showTimeSelect
          placeholderText={`Selecione uma data para agendar ${queryType === "campaign" ? "sua Campanha": "seu Momentum"}`}
          timeFormat="HH:mm"
          timeIntervals={15}
          timeCaption="Hora"
          dateFormat="dd/MM/yyyy - HH:mm"
          isCleareable
        />
        <div className="flex flex--center mgtop-25">
          <button
            type="button"
            className="button button--uppercase button--blue"
            onClick={() => { setModal(false); saveMomentum(data, domain, 'Paused', scheduleTime.parsed, queryType) }}
          >
            Agendar
          </button>
        </div>
      </div>
    </Modal>
  </>)
}